import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ChevronGlyph} from '@/components/ui/icons'
import {Input} from '@/components/ui/input'
import {SearchComboboxShell} from '@/components/search-combobox-shell'
import {useCiiuSearch, useRiskOptions, type CiiuCode} from '@/hooks/common/referencias/use-ciiu'
import {useDebouncedValue} from '@/hooks/common/use-debounced-value'
import {RISK_CLASS_ROMAN, formatRatePct} from '@/lib/risk'
import {tope} from '@/lib/campos-texto'

/** Decreto 768 risk options for a CIIU, afín-resolved. */
export function RiskOptionsPicker({
  ciiuCode,
  riskId,
  onPick,
}: {
  ciiuCode: string
  riskId: string
  onPick: (id: string) => void
}) {
  const {t} = useTranslation()
  const {data: options} = useRiskOptions(ciiuCode)

  if (!options) return null
  if (options.length === 0) return <p className="text-sm text-muted-foreground">{t('business.risk.noneApplicable')}</p>
  return (
    <ul className="max-h-44 space-y-1.5 overflow-y-auto pr-1" data-testid="risk-options">
      {options.map((option) => (
        <li key={option.id}>
          <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-input px-2.5 py-2.5 has-checked:border-primary">
            <input
              type="radio"
              name="risk_option"
              value={option.id}
              checked={riskId === option.id}
              onChange={() => onPick(option.id)}
              className="mt-0.5 accent-primary"
            />
            <span className="min-w-0 text-sm">
              <span className="font-mono text-xs font-medium text-foreground">
                {t('business.risk.subCodeLabel', {code: option.sub_code})} ·{' '}
                {t('business.risk.classValue', {roman: RISK_CLASS_ROMAN[option.risk_class]})} ·{' '}
                {formatRatePct(option.initial_rate_pct)}
              </span>
              <span className="mt-0.5 line-clamp-3 block text-xs text-muted-foreground">{option.description}</span>
            </span>
          </label>
        </li>
      ))}
    </ul>
  )
}

export function CiiuPicker({ciiu, onChange}: {ciiu: CiiuCode | null; onChange: (ciiu: CiiuCode | null) => void}) {
  const {t} = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebouncedValue(query, 200)
  const {data: results} = useCiiuSearch(debouncedQuery)

  function pick(next: CiiuCode | null) {
    onChange(next)
    setOpen(false)
  }

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) setQuery('')
  }

  return (
    <SearchComboboxShell
      title={t('business.ciiu.select')}
      open={open}
      onOpenChange={handleOpenChange}
      trigger={
        <button
          type="button"
          id="ciiu_trigger"
          role="combobox"
          aria-expanded={open}
          className="flex h-11 w-full items-center justify-between gap-2 rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground md:h-9">
          {ciiu ? (
            <span className="min-w-0 truncate">
              <span className="font-mono">{ciiu.code}</span>
              <span className="text-muted-foreground"> — {ciiu.description}</span>
            </span>
          ) : (
            <span className="min-w-0 truncate text-muted-foreground">{t('business.ciiu.select')}</span>
          )}
          <ChevronGlyph size={14} className="opacity-60" />
        </button>
      }>
      <Input
        maxLength={tope('BUSCA')}
        id="ciiu_search"
        placeholder={t('business.ciiu.searchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoComplete="off"
        autoFocus
        className="h-12 shrink-0 rounded-none rounded-b-none border-0 border-b border-border text-base focus-visible:ring-0 md:h-9 md:text-sm"
      />
      <ul className="flex-1 overflow-y-auto p-1 md:max-h-56 md:flex-none">
        {ciiu && (
          <li>
            <button
              type="button"
              className="w-full rounded-md px-2 py-3 text-left text-sm text-muted-foreground hover:bg-secondary md:py-1.5"
              onClick={() => pick(null)}>
              {t('business.ciiu.remove')}
            </button>
          </li>
        )}
        {query.trim() &&
          results?.items.map((code) => (
            <li key={code.code}>
              <button
                type="button"
                className="w-full break-words rounded-md px-2 py-3 text-left text-sm hover:bg-secondary md:py-1.5"
                onClick={() => pick(code)}>
                <span className="font-mono text-xs font-medium">{code.code}</span>{' '}
                <span className="text-muted-foreground">{code.description}</span>
              </button>
            </li>
          ))}
        {query.trim() && results?.items.length === 0 && (
          <li className="px-2 py-1.5 text-sm text-muted-foreground">{t('business.ciiu.noResults')}</li>
        )}
        {!query.trim() && !ciiu && (
          <li className="px-2 py-1.5 text-sm text-muted-foreground">{t('business.ciiu.searchHint')}</li>
        )}
      </ul>
    </SearchComboboxShell>
  )
}
